import { Box, Divider, TextField, useTheme } from '@mui/material';
import { RHFAutocomplete, RHFTextField } from 'src/components/minimals/form';
import { inputFiled } from '../../pages/style';
import { useFormContext } from 'react-hook-form';
import {
  PDF_SITE_STATUS_TEMPLATE_DROPDOWN,
  SiteDropdownOption,
} from '../../config/pdfReportConfig';
import { ITimezoneOption } from 'react-timezone-select';
import { PDFReportAddTag } from '../pdf-report-template-form-add-tag/PDFReportAddTag';
import TimeFrameDropdown from '../sidebar/form-inputs/TimeFrameDropdown';
import { timeFrameOptions } from '../../utils/timeFrameToDateUtils';
import { ReportType } from '../../types';
import { TimeFrameRangePicker } from '../sidebar/form-inputs/TimeFrameRangePicker';
import { useResponsive } from 'src/hooks/useResponsive';

type props = {
  sites: SiteDropdownOption[] | undefined;
  options: ITimezoneOption[];
  assets: {
    loaded: boolean;
    data: {
      label: string;
      value: string;
    }[];
  };
  layout: string;
  disabled?: boolean;
};
export const SiteStatusReportForm = ({ sites, options, layout, disabled }: props) => {
  const REQUIRED_TEXT_FIELD = 'The field is required';
  const theme = useTheme();

  const { watch, setValue, clearErrors } = useFormContext();
  const isMobile = useResponsive('down', 'md');
  const methods = useFormContext();

  const formErrors = methods.formState.errors?.data as Record<string, any> | undefined;
  return (
    <>
      <Box display="grid" gridTemplateColumns={layout} gap={3} sx={{ mb: 2 }}>
        <TextField
          value="PDF Data Report"
          disabled={true}
          size="small"
          sx={{ width: '100%' }}
          label="Report Type"
        />
        <RHFAutocomplete
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          value={watch('header.reportTemplate')}
          name="header.reportTemplate"
          options={PDF_SITE_STATUS_TEMPLATE_DROPDOWN}
          rules={{ required: REQUIRED_TEXT_FIELD }}
          disableClearable
          disabled={true}
          freeSolo
          size="small"
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              inputProps={{
                ...p.inputProps,
                'data-sm': 'report-template',
              }}
              fullWidth
              label={'Report Template *'}
            />
          )}
        />
        <RHFAutocomplete
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          name="data.siteId"
          disabled={disabled}
          value={watch('data.siteId')}
          onChange={(_, newVal) => {
            setValue('data.siteId', newVal);
            setValue('data.tagList', []);
            clearErrors('data.siteId');
          }}
          options={sites || []}
          rules={{ required: REQUIRED_TEXT_FIELD }}
          disableClearable
          size="small"
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              error={!!formErrors?.siteId}
              helperText={formErrors?.siteid?.message}
              inputProps={{
                ...p.inputProps,
                'data-sm': 'site',
              }}
              fullWidth
              label={'Site *'}
            />
          )}
        />
        <RHFAutocomplete
          freeSolo
          sx={{
            ...inputFiled,
            width: '100%',
          }}
          name="data.timeZone"
          rules={{ required: REQUIRED_TEXT_FIELD }}
          options={options}
          disableClearable
          disabled={disabled}
          value={watch('data.timeZone.value')}
          onChange={(_, newVal) => {
            setValue('data.timeZone', newVal);
            clearErrors('data.timeZone');
          }}
          size="small"
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.value}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                'data-sm': 'time-zone',
              }}
              error={!!formErrors?.timeZone}
              helperText={formErrors?.timeZone?.message}
              fullWidth
              label={'Time Zone *'}
            />
          )}
        />

        <TimeFrameDropdown
          disabled={disabled}
          value={watch('data.timeFrame')}
          onChange={(val: any) => setValue('data.timeFrame', val)}
          testId="report-time-frame"
          timeFrames={timeFrameOptions}
        />
        {Array.isArray(watch('data.timeFrame')) && (
          <TimeFrameRangePicker
            disabled={disabled}
            value={watch('data.timeFrame')}
            onChange={(val) => setValue('data.timeFrame', val)}
            reportType={ReportType.PDF_SITE_STATUS_REPORT}
            isMobile={isMobile}
          />
        )}

        <RHFTextField
          label="Tolerance (%) *"
          disabled={disabled}
          name="data.tolerance"
          type="number"
          testId="tolerance-field"
          rules={{
            required: REQUIRED_TEXT_FIELD,
            min: { value: 1, message: 'Value must be greater than 0' },
          }}
          size="small"
          inputProps={{
            inputProps: {
              min: 0,
              step: 0.1,
            },
          }}
          onKeyDown={(e) => {
            clearErrors('data.tolerance');
          }}
        />
      </Box>
      <Divider sx={{ gridColumn: '1/-1' }} />
      <PDFReportAddTag
        disabled={disabled}
        selectedSiteId={watch('data.siteId.value')}
        onChange={(newTagList) => setValue('data.tagList', newTagList)}
      />
    </>
  );
};
